import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { cn } from '@helpers/cn';
import { useLocalStorageState } from '@helpers/useLocalStorageState';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import type { TAsrExecution } from 'app/API/custom/asr-execution/types';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createFetchAsrTranscriptionQueryFn } from '../../api/fetchAsrTranscription';
import type { AsrCreateTranscriptionResponse, AsrRetrieveTranscriptionResponse, AsrTranscript } from '../../api/types';
import { getTranscriptRouteOutcome } from '../../helpers/getTranscriptRouteOutcome';
import { getProviderTranscriptId } from '../../helpers/getProviderTranscriptId';
import { isTerminalTranscriptionStatus } from '../../helpers/isTerminalTranscriptionStatus';
import { useAsrProxyConsoleContext } from '../../providers/AsrProxyConsoleProvider';
import type { TPlaygroundRoute } from '../../types/TPlaygroundRoute';
import type { TTranscriptionRouteMode } from '../../types/TTranscriptionRouteMode';
import { LookupPanel } from './components/LookupPanel';
import { ResultView } from './components/ResultView';
import { SubmitPanel } from './components/SubmitPanel';
import { TranscriptionRouteAnimation } from './components/TranscriptionRouteAnimation';

const RESULT_POLL_INTERVAL_MS = 1500;

export const PlaygroundTab = () => {
  const { call } = useAPI();
  const { preload, clearPreload } = useAsrProxyConsoleContext();
  const [model, setModel] = useLocalStorageState('asr-playground:model', '');
  const [resultId, setResultId] = useLocalStorageState('asr-playground:resultId', '');
  const [page, setPage] = useLocalStorageState('asr-playground:page', 0);
  const [lookupId, setLookupId] = useLocalStorageState('asr-playground:lookupId', '');
  const [lookupProvider, setLookupProvider] = useLocalStorageState('asr-playground:lookupProvider', 'assemblyai');
  const [lookupProviderTranscriptId, setLookupProviderTranscriptId] = useLocalStorageState(
    'asr-playground:providerTranscriptId',
    '',
  );
  const [result, setResult] = useState<AsrTranscript | null>(null);
  const [resultExecution, setResultExecution] = useState<TAsrExecution | null>(null);
  const [resultJobId, setResultJobId] = useState('');
  const [error, setError] = useState('');
  const [parametersOpen, setParametersOpen] = useState(false);
  const [pendingJobId, setPendingJobId] = useLocalStorageState('asr-playground:pendingJobId', '');
  const [route, setRoute] = useState<TPlaygroundRoute | null>(null);
  const routeRunCountRef = useRef(0);
  const nextRouteRunId = () => {
    routeRunCountRef.current += 1;
    return routeRunCountRef.current;
  };

  const rehydrateQuery = useQuery({
    queryKey: asrProxyKeys.transcription(resultId),
    queryFn: createFetchAsrTranscriptionQueryFn(call),
    enabled: !preload && !result && pendingJobId === '' && resultId !== '',
  });
  const rehydratedResult =
    rehydrateQuery.data?.transcript && rehydrateQuery.data.id === resultId ? rehydrateQuery.data : null;
  const displayedResult = result ?? rehydratedResult?.transcript ?? null;
  const displayedExecution = resultExecution ?? rehydratedResult?.execution ?? null;

  const pendingQuery = useQuery({
    queryKey: asrProxyKeys.transcription(pendingJobId),
    queryFn: createFetchAsrTranscriptionQueryFn(call),
    enabled: pendingJobId !== '',
    refetchInterval: (query) =>
      isTerminalTranscriptionStatus(query.state.data?.status) ? false : RESULT_POLL_INTERVAL_MS,
  });

  const showResult = (response: AsrRetrieveTranscriptionResponse) => {
    if (!response.transcript) {
      return;
    }
    setError('');
    setResult(response.transcript);
    setResultExecution(response.execution ?? null);
    setResultJobId(response.id);
    setResultId(response.id);
    setPage(0);
  };

  const showFailedJob = (response: AsrRetrieveTranscriptionResponse, message: string) => {
    setResult(null);
    setResultId('');
    setResultExecution(response.execution ?? null);
    setResultJobId(response.id);
    setError(message);
  };

  const openRoute = (
    mode: TTranscriptionRouteMode,
    jobId: string,
    execution: TAsrExecution,
    transcript: AsrTranscript | null,
    errorMessage: string,
    audioUrl?: string,
  ) => {
    setRoute({
      runId: nextRouteRunId(),
      jobId,
      mode,
      execution,
      audioUrl,
      outcome: getTranscriptRouteOutcome(transcript?.status ?? (errorMessage ? 'error' : 'completed')),
      transcript,
      errorMessage,
    });
  };

  const openJobResponse = useCallback(
    (response: AsrRetrieveTranscriptionResponse) => {
      setLookupId(response.id);
      setLookupProviderTranscriptId(getProviderTranscriptId(response));
      setLookupProvider(response.execution?.plan.provider ?? response.transcript?.provenance?.provider ?? 'assemblyai');
      setResultJobId(response.id);
      setResultExecution(response.execution ?? null);
      if (!isTerminalTranscriptionStatus(response.status)) {
        setResult(null);
        setResultId('');
        setError('');
        setPendingJobId(response.id);
        setRoute(
          response.execution
            ? {
                runId: nextRouteRunId(),
                jobId: response.id,
                mode: 'live',
                execution: response.execution,
                outcome: 'awaiting-response',
                transcript: null,
                errorMessage: '',
              }
            : null,
        );
        return;
      }
      setPendingJobId('');
      setRoute(null);
      if (response.transcript) {
        setError('');
        setResult(response.transcript);
        setResultId(response.id);
        setPage(0);
      } else {
        setResult(null);
        setResultId('');
        setError(response.error ?? `Job ${response.id} has no transcript (status ${response.status}).`);
      }
    },
    [setLookupId, setLookupProvider, setLookupProviderTranscriptId, setPage, setPendingJobId, setResultId],
  );

  useEffect(() => {
    if (!preload) {
      return;
    }
    openJobResponse(preload.response);
    setPage(0);
    if (preload.model) {
      setModel(preload.model);
    }
    clearPreload();
  }, [preload, clearPreload, openJobResponse, setModel, setPage]);

  const showError = (message: string) => {
    setResult(null);
    setResultExecution(null);
    setResultJobId('');
    setResultId('');
    setError(message);
  };

  useEffect(() => {
    const response = pendingJobId ? pendingQuery.data : rehydratedResult;
    if (!response) {
      return;
    }
    setLookupId(response.id);
    setLookupProviderTranscriptId(getProviderTranscriptId(response));
    setLookupProvider(response.execution?.plan.provider ?? response.transcript?.provenance?.provider ?? 'assemblyai');
  }, [
    pendingJobId,
    pendingQuery.data,
    rehydratedResult,
    setLookupId,
    setLookupProvider,
    setLookupProviderTranscriptId,
  ]);

  useEffect(() => {
    const response = pendingQuery.data;
    if (pendingJobId === '' || !response || !isTerminalTranscriptionStatus(response.status)) {
      return;
    }
    setPendingJobId('');
    setLookupId(response.id);
    const outcome = getTranscriptRouteOutcome(response.status);
    const errorMessage = response.error ?? response.transcript?.error ?? `Job ${response.id} ended with an error.`;
    if (response.transcript && response.status === 'completed') {
      showResult(response);
    } else {
      showFailedJob(response, errorMessage);
    }
    setRoute((current) =>
      current && current.jobId === response.id
        ? {
            ...current,
            execution: response.execution ?? current.execution,
            outcome,
            transcript: response.transcript ?? null,
            errorMessage: outcome === 'response-failed' ? errorMessage : '',
          }
        : current,
    );
  }, [pendingJobId, pendingQuery.data]);

  useEffect(() => {
    if (pendingJobId === '' || !pendingQuery.isError) {
      return;
    }
    setPendingJobId('');
    const message = pendingQuery.error instanceof Error ? pendingQuery.error.message : String(pendingQuery.error);
    showError(message);
    setRoute((current) => (current ? { ...current, outcome: 'response-failed', errorMessage: message } : current));
  }, [pendingJobId, pendingQuery.error, pendingQuery.isError]);

  const startTranscriptionRoute = (response: AsrCreateTranscriptionResponse, audioUrl: string) => {
    setError('');
    setResult(null);
    setResultId('');
    setResultExecution(null);
    setResultJobId(response.id);
    setLookupProviderTranscriptId(getProviderTranscriptId(response));
    setLookupProvider(response.provider);
    setPendingJobId(response.id);
    setLookupId(response.id);
    if (!response.execution) {
      return;
    }
    setRoute({
      runId: nextRouteRunId(),
      jobId: response.id,
      mode: 'live',
      execution: response.execution,
      audioUrl,
      outcome: getTranscriptRouteOutcome(response.status),
      transcript: response.transcript ?? null,
      errorMessage: '',
    });
  };

  const routeJobId = resultJobId || rehydratedResult?.id || '';
  const canShowRoute = displayedExecution !== null && routeJobId !== '';

  const showTranscriptRoute = () => {
    if (!displayedExecution || !canShowRoute) {
      return;
    }
    openRoute('review', routeJobId, displayedExecution, displayedResult, displayedResult?.error ?? error);
  };

  const closeTranscriptionRoute = useCallback(() => setRoute(null), []);

  if (route) {
    return (
      <section className="flex min-h-[calc(100vh-136px)] min-w-0 w-full rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)] xl:h-[calc(100vh-136px)] xl:min-h-[620px] xl:overflow-hidden">
        <TranscriptionRouteAnimation
          runId={route.runId}
          jobId={route.jobId}
          mode={route.mode}
          execution={route.execution}
          audioUrl={route.audioUrl}
          outcome={route.outcome}
          transcript={route.transcript}
          errorMessage={route.errorMessage}
          onAnimationFinished={closeTranscriptionRoute}
          onClose={closeTranscriptionRoute}
        />
      </section>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 xl:h-[calc(100vh-136px)] xl:min-h-[620px] xl:grid-cols-[minmax(280px,35fr)_minmax(0,65fr)]">
      <div className={cn('grid gap-5 xl:min-h-0', parametersOpen ? 'xl:grid-rows-1' : 'xl:grid-rows-2')}>
        <SubmitPanel
          model={model}
          setModel={setModel}
          isWaitingForResult={pendingJobId !== ''}
          onTranscriptionStart={startTranscriptionRoute}
          onError={showError}
          onParametersOpenChange={setParametersOpen}
        />
        {!parametersOpen ? (
          <LookupPanel
            id={lookupId}
            provider={lookupProvider}
            providerTranscriptId={lookupProviderTranscriptId}
            onIdChange={setLookupId}
            onProviderChange={setLookupProvider}
            onProviderTranscriptIdChange={setLookupProviderTranscriptId}
            onResult={openJobResponse}
            onError={showError}
          />
        ) : null}
      </div>
      <section className="min-h-[520px] rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)] xl:flex xl:min-h-0 xl:flex-col xl:overflow-hidden">
        <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-foreground">Transcript result</h2>
            <p className="mt-1 text-sm text-foreground-muted">
              Normalized AssemblyAI-shaped transcript, provenance, quality checks and raw JSON.
            </p>
          </div>
          {canShowRoute ? (
            <OutlinedButton className="!w-auto" onClick={showTranscriptRoute}>
              Show route
            </OutlinedButton>
          ) : null}
        </div>
        {pendingJobId !== '' ? (
          <p role="status" className="mb-3 text-sm text-foreground-muted">
            Waiting for job {pendingJobId} to finish…
          </p>
        ) : null}
        <ResultView result={displayedResult} error={error} page={page} setPage={setPage} />
      </section>
    </div>
  );
};
